import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const services = [
  {
    name: 'Diagrammed Clarity Map',
    timeBox: 'Two working weeks',
    format: 'Async-first, one kickoff, one closeout',
    bestFor: 'A messy finance workflow, unclear handoffs, or numbers you do not trust yet.',
    output: 'Source map, drift map, evidence gaps, ranked next moves.',
    boundary: 'No build. No system changes. The map is the deliverable.',
  },
  {
    name: 'Decision Map',
    timeBox: 'One working week',
    format: 'Async-first, one kickoff, one closeout',
    bestFor: 'A concrete choice: build or buy, migrate or hold, hire or automate.',
    output: 'Options, evidence map, risk map, decision matrix, recommended posture.',
    boundary: 'You get a decision structure, not an open-ended advisory retainer.',
  },
  {
    name: 'Operator-Build Support',
    timeBox: 'Two to six working weeks per sprint',
    format: 'Written notes, check-in, handover walkthrough',
    bestFor: 'A specific scoped change where the right move is to build, not just advise.',
    output: 'The build, verification notes, handover document, boundary notes.',
    boundary: 'Usually after a Map or a clear paid scoping pass. No retainer drift.',
  },
];

const notIncluded = [
  'Tax filing, audit, legal, or licensed financial advice',
  'Bookkeeping authority or running anyone\'s books',
  'Vendor or provider contact on your behalf',
  'Production system writes without per-action permission',
  'Anything you mark off-limits in our engagement letter',
];

const process = [
  {
    step: '01',
    title: 'Async prefit',
    body: 'Send written, non-sensitive context. I check fit before calendar time enters the picture.',
  },
  {
    step: '02',
    title: 'Fixed-fee engagement',
    body: 'If the problem fits, I send a proposal with scope, time box, deliverables, and fee.',
  },
  {
    step: '03',
    title: 'Right next move',
    body: 'Engagements end cleanly. No implicit retainer, no auto-renew, no scope drift.',
  },
];

export default function Services() {
  return (
    <div className="bg-paper">
      <section id="engagements" data-guide-label="Engagements" className="mx-auto max-w-5xl px-6 py-24">
        <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
          How I work
        </p>
        <h1 className="mt-3 font-display text-4xl leading-tight tracking-tight md:text-5xl">
          Three ways to work. Clear scope. Clean handoff.
        </h1>
        <p className="mt-4 max-w-2xl text-lg text-ink/70">
          We start with written fit, choose the smallest useful engagement,
          and end with a named next move. No implicit retainer, no auto-renew,
          no "while we're at it" expansion.
        </p>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {services.map((service) => (
            <Card key={service.name} className="flex h-full flex-col">
              <CardContent className="flex h-full flex-col p-8">
                <p className="font-display text-xs uppercase tracking-[0.2em] text-accent">
                  {service.name}
                </p>
                <p className="mt-3 text-xs text-ink/55">{service.timeBox}</p>
                <p className="mt-1 text-xs text-ink/55">{service.format}</p>
                <div className="mt-6 grid gap-5 text-sm text-ink/72">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">
                      Best for
                    </p>
                    <p className="mt-2">{service.bestFor}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">
                      You get
                    </p>
                    <p className="mt-2">{service.output}</p>
                  </div>
                </div>
                <div className="mt-6 border-t border-ink/10 pt-4 text-sm italic text-ink/60">
                  <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">
                    Boundary
                  </p>
                  <p className="mt-2">{service.boundary}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-4 border-t border-ink/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-xl text-sm italic text-ink/60">
            Unsure which one fits? I will route it.
          </p>
          <Button asChild>
            <Link to="/work-with-me#prefit">Start with the fit check</Link>
          </Button>
        </div>
      </section>

      <section id="boundaries" data-guide-label="Boundaries" className="border-t border-ink/5 bg-paper">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
            What I don't do
          </p>
          <h2 className="mt-3 font-display text-3xl tracking-tight md:text-4xl">
            Clear scope protects the work.
          </h2>
          <ul className="mt-8 grid gap-3 text-ink/70 md:grid-cols-2">
            {notIncluded.map((item) => (
              <li key={item} className="flex gap-3">
                <span className="text-accent">x</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 max-w-2xl text-sm text-ink/60">
            If you need work outside what I do, I'll say so when you submit the
            intake — and refer you onward if I know someone better suited.
          </p>
        </div>
      </section>

      <section id="start-process" data-guide-label="How we start" className="border-t border-ink/5 bg-ink text-paper">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
            How we start
          </p>
          <h2 className="mt-3 font-display text-3xl tracking-tight md:text-4xl">
            Written fit first. Calendar second.
          </h2>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {process.map((p) => (
              <div key={p.step}>
                <p className="font-display text-5xl text-accent">{p.step}</p>
                <h3 className="mt-4 font-display text-xl">{p.title}</h3>
                <p className="mt-2 text-paper/70">{p.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Button asChild size="lg" variant="accent">
              <Link to="/work-with-me#prefit">Start async prefit</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
